import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TextButton from 'LuxKit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ShareButtons from 'marketing/components/Competitions/ShareButtons'
import { useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { COMP_RAFFLE_TYPE } from 'marketing/components/Competitions/constants'
import { formatRoundedAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import GeoContext from 'contexts/geoContext'

const RewardTile = styled(VerticalSpacer)`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  padding: ${rem(20)};
  border: 2px solid ${props => props.theme.palette.neutral.surface.darkForeground};
  border-radius: ${props => props.theme.borderRadius.M};
  margin: ${rem(16)};
  overflow: hidden;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${mediaQueryUp.tablet} {
    padding: ${rem(6)} ${rem(6)};
  }

  ${mediaQueryUp.desktop} {
    padding: ${rem(16)} ${rem(16)};
  }
`

const Divider = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.lightForeground};
  height: 1px;
  width: 100%;
`

const PromoCodeWrapper = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  color: ${props => props.theme.palette.neutral.surface.lightContrast};
  padding: ${rem(16)} ${rem(16)};
  width: 100%;
`

const StyledClickableLink = styled(ClickableLink)`
  text-decoration: underline;
  display: inline-block;
`

interface Props {
  isAnimationFinished: boolean
  isModalOpen: boolean
  closeModal: () => void
  prize: any
  drawDate: string
  promoLink?: string
  breadcrumb: any
  competitionTitle: string
}

function PrizeModal({
  isAnimationFinished,
  isModalOpen,
  closeModal,
  prize,
  drawDate,
  promoLink,
  breadcrumb,
  competitionTitle,
}: Props) {
  const intl = useIntl()
  const deriveDiscountValue = prize?.prizeDetails?.discountValue
  const isRaffle = prize?.prizeType === COMP_RAFFLE_TYPE

  const headingText = isRaffle
    ? intl.formatMessage(
        {
          id: 'oCvE9dqtcBEymKmRzRRK',
          defaultMessage: '{prizeValue} entries',
          description: 'Number of raffle entries won displayed in prize modal heading',
        },
        { prizeValue: prize?.prizeValue },
      )
    : intl.formatMessage(
        {
          id: 'rL3rsLJnhoKBfJlKRM5S',
          defaultMessage: '{discountValue}% off',
          description: 'Discount percentage off displayed in prize modal heading',
        },
        { discountValue: deriveDiscountValue },
      )

  const subtitleText = isRaffle
    ? intl.formatMessage({
        id: 'XLd7Rc3YS3q0Y9zMtxcB',
        defaultMessage: 'to the draw!',
        description: 'Subtitle below entry count in prize modal dialog window',
      })
    : intl.formatMessage({
        id: 'Qh7bVzyMBmPM02aaLRKH',
        defaultMessage: 'towards your next escape!',
        description: 'Subtitle below discount amount in the prize modal in a modal dialog',
      })

  const bodyTextLine1 = isRaffle
    ? intl.formatMessage(
        {
          id: '3VyoH6pBhjkEu8vE1q6y',
          defaultMessage: 'Draw Date: {drawDate}',
          description: 'Draw date display in prize modal for raffle competitions',
        },
        { drawDate },
      )
    : intl.formatMessage(
        {
          id: 'HRU02kYMy_Zt8aIhTM6S',
          defaultMessage: 'Your promo code for {discountValue}%* off to claim on your next escape has also been',
          description: 'First line of promo code claim message in prize modal',
        },
        { discountValue: deriveDiscountValue },
      )

  const bodyTextLine2 = isRaffle
    ? undefined
    : intl.formatMessage({
        id: 'nHmGIphWzGlk35QTBOIt',
        defaultMessage: 'sent to your email.',
        description: 'Second line of promo code claim message in prize modal',
      })

  const { currentCurrency: currencyCode, currentRegionCode: regionCode } = useContext(GeoContext)

  const minMaxDiscountText = isRaffle ? null : intl.formatMessage(
    {
      id: 'leKPJix5ZXFNZ4mKcHav',
      defaultMessage: '*Minimum spend {minSpend}. Maximum discount {maxDiscount}.',
      description: 'Minimum spend and maximum discount terms in prize modal',
    },
    {
      minSpend: formatRoundedAmountWithDollarType(prize?.prizeDetails?.minSpend, currencyCode, regionCode),
      maxDiscount: formatRoundedAmountWithDollarType(prize?.prizeDetails?.maxDiscount, currencyCode, regionCode),
    },
  )

  // navigate to homepage if there is no offer link
  const navigateToOffers = promoLink || '/'

  const browseExclusiveOffersLabel = intl.formatMessage({
    id: '3k9X6mObqjjrgjJwBqqP',
    defaultMessage: 'Feel free to continue browsing our exclusive offers',
    description: 'Message encouraging users to browse offers after raffle entry in prize modal',
  })
  const enterPromoLabel = intl.formatMessage({
    id: 'GIVBha2w2oGVG8E9fIJa',
    defaultMessage: 'Enter promo code at checkout:',
    description: 'Label above promo code in prize modal during the checkout and payment process',
  })
  const browseOffersLabel = intl.formatMessage({
    id: 'UjKGnLG652QgXjG5Ay47',
    defaultMessage: 'Browse offers',
    description: 'Button text linking to offers page from prize modal',
  })
  const shareResultLabel = intl.formatMessage({
    id: 'Tni-ycvBQNGQct7prQ_V',
    defaultMessage: 'Share my result',
    description: 'Heading above social share buttons in the prize modal',
  })
  const modalTitle = intl.formatMessage({
    id: 'gu_y86ZugxI3AUNpvO6I',
    defaultMessage: 'Congratulations! You have won:',
    description: 'Title of the prize modal after winning a competition',
  })

  const prizeModalMessage = isRaffle ?
      <BodyText variant="medium" align="center">{browseExclusiveOffersLabel}</BodyText> :
      <PromoCodeWrapper>
        <Caption variant="large" align="center"> {enterPromoLabel} </Caption>
        <BodyText variant="large" align="center" weight="bold"> {prize?.prizeValue}</BodyText>
      </PromoCodeWrapper>

  return (
    <ModalBase isOpen={isAnimationFinished && isModalOpen} onClose={closeModal}>
      <ModalHeader title={modalTitle} onCloseButtonClick={closeModal} />
      <RewardTile gap={20}>
        <LineGiftIcon size={54} />
        <div>
          <Heading variant="heading2" align="center">{headingText}</Heading>
          <BodyText variant="large" align="center">{subtitleText}</BodyText>
        </div>
        {(bodyTextLine1 || bodyTextLine2) && <div>
          {[bodyTextLine1, bodyTextLine2].map((text, index) => text && <BodyText key={`${text}-${index}`} variant="medium" align="center">{text}</BodyText>)}
        </div>}
        <Divider />
        {prizeModalMessage}
        <TextButton
          kind="primary"
          data-url={navigateToOffers}
          to={navigateToOffers}
          rel="noopener noreferrer"
          target="_blank"
          className="hide-on-animation"
          fit="full-width"
        >{browseOffersLabel}
        </TextButton>
        {minMaxDiscountText && <div>
          <Caption variant="medium" colour="light-foreground-alt" align="center">{minMaxDiscountText}</Caption>
          <Caption variant="medium" colour="light-foreground-alt" align="center">
            <FormattedMessage
              id="kN8swVZ1-8MF77lZp_-0"
              defaultMessage="<link>Terms and conditions</link> apply"
              description="Terms and conditions link in prize modal dialog window"
              values={{
                link: (chunks) => <StyledClickableLink to="/promotions" target="_blank">{chunks}</StyledClickableLink>,
              }}
            />
          </Caption>
        </div>}
        <VerticalSpacer gap={12}>
          <BodyText variant="large" weight="bold" align="center">{shareResultLabel}</BodyText>
          <ShareButtons breadcrumb={breadcrumb} competitionTitle={competitionTitle} />
        </VerticalSpacer>
      </RewardTile>
    </ModalBase>
  )
}

export default PrizeModal
